import React, { useState, useEffect } from "react";
import Navbar from "../components/Navbar";
import Sidebar from "../components/Sidebar";
import { fetchNoc, submitNocRequest, fetchNocCertificateUrl } from "../api/api";
import { usePlots } from "../context/PlotContext";

const PURPOSES = [
  { value: "construction", label: "Construction" },
  { value: "bank_loan", label: "Bank Loan" },
  { value: "plot_transfer", label: "Plot Transfer" },
  { value: "other", label: "Other Purpose" },
];

const STATUS_LABELS = {
  submitted: "Submitted",
  doc_verification: "Document Verification",
  legal_review: "Legal Review",
  approved: "Approved",
  rejected: "Rejected",
};

const STATUS_STYLES = {
  submitted: { background: "#fef3c7", color: "#92400e" },
  doc_verification: { background: "#dbeafe", color: "#1e40af" },
  legal_review: { background: "#dbeafe", color: "#1e40af" },
  approved: { background: "#dcfce7", color: "#166534" },
  rejected: { background: "#fee2e2", color: "#991b1b" },
};

function purposeLabel(purpose) {
  return PURPOSES.find((p) => p.value === purpose)?.label || purpose;
}

function errorMessage(err) {
  return err.response?.data?.detail || "Something went wrong. Please try again.";
}

function CustomerNOC() {

  const { plots, selectedPlot: plot, selectedPlotId, selectPlot, loading: plotsLoading } = usePlots();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const [purpose, setPurpose] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");
  const [formSuccess, setFormSuccess] = useState("");

  const [downloadingId, setDownloadingId] = useState(null);

  const load = async () => {
    setLoading(true);
    setLoadError("");
    try {
      const requestList = await fetchNoc();
      setRequests(requestList);
    } catch (err) {
      setLoadError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleSubmit = async () => {
    setFormError("");
    setFormSuccess("");

    if (!plot) {
      setFormError("You don't have a registered plot yet.");
      return;
    }
    if (!purpose) {
      setFormError("Please select a purpose.");
      return;
    }

    setSubmitting(true);
    try {
      await submitNocRequest({ plot_id: plot.id, purpose });
      setPurpose("");
      setFormSuccess("Your NoC request has been submitted — you'll get an email confirmation shortly.");
      await load();
    } catch (err) {
      setFormError(errorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDownload = async (nocId) => {
    setDownloadingId(nocId);
    try {
      const url = await fetchNocCertificateUrl(nocId);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (err) {
      window.alert(errorMessage(err));
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <>
      <Navbar />
      <Sidebar />

      <div className="main-content">

        <div className="customer-noc-page">

          <h2>Request NOC</h2>

          {(loading || plotsLoading) && <p>Loading…</p>}
          {loadError && <p className="login-error" style={{ color: "red" }}>{loadError}</p>}

          {!loading && !plotsLoading && !loadError && (
            <>

              {plots.length > 1 ? (
                <div style={{ marginBottom: "10px" }}>
                  <label style={{ fontWeight: 600, marginRight: "8px" }}>Plot</label>
                  <select value={selectedPlotId || ""} onChange={(e) => selectPlot(Number(e.target.value))}>
                    {plots.map((p) => <option key={p.id} value={p.id}>{p.plot_number}</option>)}
                  </select>
                </div>
              ) : (
                <p className="noc-subtitle">
                  {plot ? `Plot ${plot.plot_number}` : "No registered plot on file"}
                </p>
              )}

              <div className="noc-form-card">

                {formError && <p className="login-error" style={{ color: "red" }}>{formError}</p>}
                {formSuccess && <p style={{ color: "#166534", fontWeight: 600 }}>{formSuccess}</p>}

                <label>Purpose</label>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", marginBottom: "16px" }}>
                  {PURPOSES.map((p) => (
                    <button
                      key={p.value}
                      type="button"
                      className={purpose === p.value ? "login-btn" : "register-btn"}
                      style={{ width: "auto", padding: "10px 16px" }}
                      onClick={() => setPurpose(p.value)}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>

                <button
                  className="submit-noc-btn"
                  onClick={handleSubmit}
                  disabled={submitting || !plot}
                >
                  {submitting ? "Submitting…" : "Submit NOC Request"}
                </button>

              </div>

              <div className="noc-status-card">

                <h3>My NOC Requests</h3>

                {requests.length === 0 && <p style={{ color: "#666" }}>No requests raised yet.</p>}

                {requests.map((r) => (
                  <div key={r.id} style={{ padding: "12px 0", borderBottom: "1px solid #eee" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <strong>{r.request_number} — {purposeLabel(r.purpose)}</strong>
                      <span
                        style={{
                          ...(STATUS_STYLES[r.status] || {}),
                          padding: "3px 10px", borderRadius: "20px", fontSize: "11px", fontWeight: 600,
                        }}
                      >
                        {STATUS_LABELS[r.status] || r.status}
                      </span>
                    </div>
                    <p style={{ margin: "6px 0", fontSize: "13px", color: "#888" }}>
                      Submitted {new Date(r.created_at).toLocaleDateString()}
                    </p>

                    {r.admin_notes && (
                      <p style={{ margin: "8px 0 0", fontSize: "13px", background: "#f5f5f5", padding: "8px", borderRadius: "6px" }}>
                        <strong>Notes:</strong> {r.admin_notes}
                      </p>
                    )}

                    {r.certificate_url && (
                      <button
                        className="view-btn"
                        style={{ marginTop: "8px" }}
                        onClick={() => handleDownload(r.id)}
                        disabled={downloadingId === r.id}
                      >
                        {downloadingId === r.id ? "…" : "⬇ Download Certificate"}
                      </button>
                    )}
                  </div>
                ))}

              </div>

            </>
          )}

        </div>

      </div>
    </>
  );
}

export default CustomerNOC;
